import { ROLE_NAMES } from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../auth/store.js';
import { db } from '../sync/db.js';
import { SyncIndicator } from './SyncIndicator.js';

/**
 * Шапка личного кабинета: кто вошёл, колокольчик, состояние обмена.
 * Одинаковая у всех ролей — различается только то, что под ней.
 */
/** Колокольчик. Рисуем сам, а не тянем библиотеку значков ради одного. */
function BellIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

export function HomeHeader({ title }: { title: string }): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const authStatus = useAuthStore((state) => state.status);
  const logout = useAuthStore((state) => state.logout);

  const unread = useLiveQuery(
    async () => db.notifications.filter((item) => !item.readAt).count(),
    [],
    0,
  );

  /**
   * Отклонённые отправки. Из шапки убраны, но молчать о них нельзя: отчёт,
   * который сервер не принял, для человека выглядит сданным. Поэтому полоса
   * прямо в кабинете — с причиной, а не одним словом «отклонено».
   *
   * Только свои: очередь лежит на устройстве, а не в учётной записи, и один
   * планшет в цеху проходит через руки нескольких человек. Мастер, зашедший
   * после оператора, видел его отказы как свои — и не понимал, откуда они.
   */
  const rejected = useLiveQuery(
    async () =>
      !user
        ? []
        : (await db.outbox.where('status').equals('rejected').toArray()).filter(
            (record) => record.event.userId === user.id,
          ),
    [user?.id],
    [],
  );

  return (
    <>
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">{title}</h1>
        <div className="flex items-center gap-2">
          {/* Колокольчик вместо слова: значок читается сразу и не спорит
              за место с индикатором отправки. Число — сколько непрочитано. */}
          <Link
            to="/notifications"
            aria-label={unread > 0 ? `Уведомления, новых: ${unread}` : 'Уведомления'}
            className={`relative flex min-h-touch min-w-touch items-center justify-center rounded-xl px-4 ${
              unread > 0 ? 'bg-sky-600 text-white' : 'bg-muted'
            }`}
          >
            <BellIcon />
            {unread > 0 && (
              <span className="ml-2 text-base font-semibold tabular-nums">{unread}</span>
            )}
          </Link>
          <SyncIndicator />
        </div>
      </header>

      <section className="mb-4 rounded-xl bg-card shadow-card p-4">
        <p className="text-lg font-semibold">{user?.fullName}</p>
        <p className="text-base text-dim">
          {user ? ROLE_NAMES[user.role] : ''}
          {authStatus === 'offline-authorized' && ' · вход без сети'}
        </p>
        {/* Кнопка одна. «Сменить сотрудника» делала ровно то же самое —
            закрывала сессию на устройстве — и только путала. */}
        <button
          type="button"
          onClick={() => void logout()}
          className="mt-4 rounded-xl bg-muted px-5 py-3 text-base font-semibold active:bg-line"
        >
          Выйти
        </button>
      </section>

      {rejected.length > 0 && (
        <Link to="/queue" className="mb-4 block rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 p-4">
          <p className="text-lg font-semibold">
            Сервер не принял: {rejected.length}
          </p>
          {/* Показываем причину первого: их обычно одна, а список целиком
              лежит на экране очереди */}
          <p className="mt-1 text-base text-rose-700">
            {rejected[0]?.rejectionReason ?? 'причина не указана'}
          </p>
          <p className="mt-2 text-base text-rose-600">Нажмите, чтобы разобрать</p>
        </Link>
      )}
    </>
  );
}
